import { Dispatch, Reducer, type PayloadAction } from '@reduxjs/toolkit';
import { mbtiDiscoAirConnectionName } from '@sccs/common';
import { InflateState, MbtiDiscoAirState, VehicleTileData } from '@sccs/types';
import { initializeLogger } from '@seamless/logger';
import { SeamlessConnection } from '@seamless/store';
import { getClientAndFetchData } from '../http/util/getClientAndFetchData';
import { buildVehicleTileData } from '../schema/VehicleTileDataBuilder';
import {
  AdditionalConfigurationData,
  SharedRequestConfiguration,
  VehicleParameterUnion,
} from '../types/GeneralParameter';
import { RawData, RawDataSchema, RequestParamsRawData } from '../types/raw/RequestParams';
import { getDataFromParameters } from '../util/GetDataFromParameters';
import { TranslationType, useI18n } from '../util/i18n';
import { getRawData } from '../util/RawDataType';

const initialState: MbtiDiscoAirState = {
  tileData: {},
};

type ArbitraryFunctionCollection = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [index: string]: (...args: any[]) => void;
};

export type Dispatchers = ReturnType<MbtiDiscoAirConnection['getPublicDispatchers']>;

type Payload = {
  inflateId: string;
  state: InflateState;
  data?: VehicleTileData;
};

type AppAction = PayloadAction<Payload>;

// Action Names
const SET_TILE_DATA = 'SET_TILE_DATA';

let logger = initializeLogger('DH-IO-SCCS/AIR', {
  tenant: 'emh',
  configs: {
    component: 'VehicleTile',
    service: 'air-tnt',
    platform: 'nebula',
    version: '',
  },
});

class MbtiDiscoAirConnection extends SeamlessConnection<ArbitraryFunctionCollection, MbtiDiscoAirState> {
  translations: (key: string, dataset?: TranslationType) => string = () => '';
  translationsLoaded?: Promise<void>;

  protected createPendingAction(inflateId: string) {
    return this.getAction(SET_TILE_DATA, { inflateId, state: 'PENDING' });
  }

  protected createFailureAction(inflateId: string) {
    return this.getAction(SET_TILE_DATA, { inflateId, state: 'FAILURE' });
  }

  protected getAction(actionType: string, payload: Payload): AppAction {
    return {
      type: this.getActionType(actionType),
      payload,
    };
  }

  constructor() {
    super(mbtiDiscoAirConnectionName);
    const { loadTranslations, t } = useI18n();
    this.translations = t;
    this.translationsLoaded = loadTranslations(logger);
  }

  get initialState() {
    return initialState;
  }

  //@ts-expect-error seamless does not support redux-thunk in types
  getReducer(): Reducer<MbtiDiscoAirState, AppAction> {
    //@ts-expect-error seamless does not support redux-thunk in types
    return (state: MbtiDiscoAirState, action: AppAction) => {
      switch (action.type) {
        case this.getActionType(SET_TILE_DATA): {
          // only set new tile data with state if the state differs
          if (state.tileData[action.payload.inflateId]?.state === action.payload.state) {
            return state;
          }
          const { inflateId, state: tileState, data } = action.payload;
          logger.log('tileData changed', inflateId, tileState, data);
          return {
            ...state,
            tileData: {
              ...state.tileData,
              [inflateId]: {
                state: tileState,
                ...data,
              },
            },
          };
        }
        default:
          return state;
      }
    };
  }

  getPublicDispatchers() {
    return {
      getData: (
        inflateIds: string[],
        config: SharedRequestConfiguration,
        requestParams: VehicleParameterUnion,
        rawData: RawData,
      ) => {
        return async (dispatch: Dispatch<AppAction>) => {
          await this.translationsLoaded;
          const { brand, ctx } = getDataFromParameters(config, requestParams);
          const additionalConfigData: AdditionalConfigurationData = {
            market: requestParams.market,
            lang: requestParams.lang,
            stage: config.stage,
            mode: config.mode,
            brand: brand,
            ctx: ctx,
            sortType: config.sortType,
          };
          logger = initializeLogger('DH-IO-SCCS/AIR', {
            tenant: 'emh',
            configs: {
              env: config.stage,
              component: 'VehicleTile',
              service: 'air-tnt',
              platform: 'nebula',
              version: '',
            },
          });
          for (const inflateId of inflateIds) {
            dispatch(this.createPendingAction(inflateId));
          }
          try {
            let response;
            if (config.isRawDataOnly) {
              const { data, schema } = (requestParams as RequestParamsRawData).rawData;
              response = await getRawData({
                schema,
                data,
              });
            } else {
              response = await getClientAndFetchData(config, requestParams, rawData);
            }
            const vehicleTileData = buildVehicleTileData(
              response,
              additionalConfigData,
              this.translations,
              rawData.schema === RawDataSchema.VEHICLE_TILE_DATA ? rawData.data : undefined,
            );
            for (const [index, inflateId] of inflateIds.entries()) {
              try {
                if (vehicleTileData[index] == null) {
                  dispatch(this.createFailureAction(inflateId));
                } else {
                  dispatch(
                    this.getAction(SET_TILE_DATA, {
                      inflateId,
                      state: 'SUCCESS',
                      data: vehicleTileData[index],
                    }),
                  );
                }
              } catch (error) {
                dispatch(this.createFailureAction(inflateId));
              }
            }
          } catch (error) {
            logger.error(error);
            for (const inflateId of inflateIds) {
              dispatch(this.createFailureAction(inflateId));
            }
          }
        };
      },
    };
  }
}

export const connection = new MbtiDiscoAirConnection();
